import {
  get,
  find,
  cloneDeep,
  filter,
  forEach,
  isNil,
  omit,
  isEmpty,
  differenceBy,
} from 'lodash';
import { v1 as uuidv1 } from 'uuid';
import arrayMove from 'array-move/index';
import {
  LOGIC_MODES,
  QUESTIONNAIRE_DEFAULT_SUBSTATE_PATHS_TO_OMIT,
} from 'containers/QuestionnairePage/constants';
import { MATRIX_OPEN_ENDED_SIGN_PLACEMENT } from 'containers/QuestionnairePage/GenericQuestion/MatrixOpenEndedQuestion/constants';

import { SHUFFLE_NONE } from 'components/DropdownShuffle/constants';
import * as PROPERTIES from 'data/nodeProperties';

import {
  getScreeningQuestionsAfterAdd,
  getScreeningQuestionsAfterRemove,
  getScreeningQuestionsAfterUpdate,
} from 'containers/TargetingPage/helpers';
import {
  updateQlAfterNodeRemove,
  updateQlAfterScreeningUpdate,
  updateState,
  getQlSubNodes,
  updateQlQuestionnaire,
  getLogicKeyFromMode,
  transformNodeToQlQuestionnaireNode,
  getNonScreeningQuestionsOrdered,
} from 'containers/QuestionnaireLogic/utils';

import {
  defaultQlService,
  QuestionnaireConditionContext,
  questionnaireLogicService,
} from 'utils/adl-services';

import {
  defaultPristineValues,
  getPredefinedABQuestions,
  getPredefinedVanWestendorpQuestions,
  getPreSelectedAttributes,
} from 'utils/questionHelpers';
import {
  QUESTION_VAN_WESTENDORP,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
  QUESTION_MATRIX_CONSTANT_SUM,
  GROUP_LOOP_MERGE,
  GROUP_BASIC,
} from 'data/nodeTypes';
import { PREDEFINED_LIKELIHOOD } from 'data/vanWestendorpQuestions';

import { getVanWestendorpRecallAnswers } from 'containers/QuestionnairePage/GenericQuestion/VanWestendorpQuestion/helpers';
import {
  hasPiping,
  replaceRecallQuestionTagsInHtmlWithText,
} from 'components/FormattedInput/helpers';
import {
  variableRefIdInLogic,
  variableValueRefIdInLogic,
} from 'modules/HiddenVariables/adl-utils';

import { QUESTIONNAIRE_REVISION_REASONS } from 'components/QuestionnaireRevisions/constants';
import { DIRECTION } from 'containers/App/constants';
import {
  isGroup,
  isAbGroup,
  isConjointOrMaxDiffGroup,
  isLoopAndMergeGroup,
  isVanWestendorpQuestionType,
  isVanWestendorpGroup,
} from 'utils/nodeTypes';

import {
  getEmptyConcept,
  getEmptyAttribute,
  getEmptyAttributeValue,
  getLoopAndMergeConcepts,
  getLoopAndMergeAttributes,
  getLoopAndMergeProperties,
  getLoopAndMergeAttributeName,
  getLoopAndMergeConceptWithName,
  getLoopAndMergeConceptName,
} from './QuestionGroup/utils';

import {
  removeAllCarryForward,
  isQuestionCarryForwarded,
  updateRelevantCarriedQuestions,
  removeLoopAndMergeBasedOnQuestionConfiguration,
} from './NodeSettings/settings/utils/CarryForward';
import {
  getNodeErrors,
  removeAllMentionsFromNode,
  updateQuestionnaireErrors,
  getNodesOnRemove,
  attachErrorsOnNode,
  updateConjointOrMaxDiffPropertiesOnRemove,
  updateGroupProperties,
  updateGroupPropertyValues,
  getRevalidatedNodesAndGlobalErrors,
  replaceMentionsRefIdWithOther,
  removeMentionsOfRemovedAnswers,
  getNodeByRefId,
  mergePreviousNodesPerNode,
  getSurveyConfigForValidate,
  extractSubstateForQuestionnaire,
  attachAIGlobalErrors,
  attachHasPipingOnNode,
  getPromptsWithoutSpecificMentionsForNode,
  hasAttributeMentionOnRefId,
  getRevalidatedQuestionsAndGlobalErrors,
  removeAttrRefIdMentionsFn,
  hasNodeMentions,
  validateQuotaAndUpdateState,
} from './utils';

export function incrementQuestionnaireEditVersion(state) {
  const questionnaireEditVersion = get(
    state,
    'surveyDetailsExtended.questionnaireEditVersion',
  );
  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
  });
}

export function saveCheckpointHandler(
  state,
  saveCheckpoint,
  saveReason = QUESTIONNAIRE_REVISION_REASONS.AUTO_SAVE,
) {
  return Object.assign({}, state, {
    revisions: {
      ...state.revisions,
      saveCheckpoint,
      saveReason,
    },
  });
}

export function setQuestionnaireShuffling(state, action) {
  const {
    surveyDetailsExtended = {},
    questionGroupShuffle: currentQuestionnaireShuffling,
    errors: questionnaireErrors,
    questionnaireLogic,
  } = state;
  const { questions: nodes = [], questionnaireEditVersion } =
    surveyDetailsExtended;

  const shuffle = get(action, 'payload.shuffle');

  let newQuestionnaireErrors = questionnaireErrors;
  let newNodes = nodes;
  let newQuestionnaireLogic = questionnaireLogic;

  // shuffling is currently disabled so we need to perform some action
  if (currentQuestionnaireShuffling === SHUFFLE_NONE) {
    ({ newNodes, newQuestionnaireErrors } = resetNodeFeatures(
      nodes,
      questionnaireErrors,
      getSurveyConfigForValidate(state),
    ));
  }

  // check if we need to change the default questionnaire logic (remove question rules etc)
  // Warning: It's likely that this function needs to run before running the resetNodeFeatures
  // since we will be effectively changing up the nodes.
  // The idea is "clear everything first, then mutate"
  if (
    currentQuestionnaireShuffling === SHUFFLE_NONE ||
    shuffle === SHUFFLE_NONE
  ) {
    newQuestionnaireLogic = resetADL({
      questionnaireLogic,
      questions: newNodes,
    });

    if (newQuestionnaireLogic.error) {
      return {
        ...state,
        questionnaireLogic: newQuestionnaireLogic,
      };
    }
  }

  return Object.assign({}, state, {
    errors: newQuestionnaireErrors,
    questionGroupShuffle: shuffle,
    questionnaireLogic: newQuestionnaireLogic,
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: newNodes,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
  });
}

function resetNodeFeatures(nodes, questionnaireErrors, surveyConfig) {
  const newNodes = [];
  let newQuestionnaireErrors = questionnaireErrors;
  nodes.forEach((node) => {
    if (isGroup(node)) {
      let group = { ...node };
      const newGroupQuestions = [];

      if (
        !isEmpty(group.loopAndMergeFromQuestionConfiguration) &&
        !group.loopAndMergeFromQuestionConfiguration.isProfiling
      ) {
        group = removeLoopAndMergeBasedOnQuestionConfiguration(group);
      }

      group.questions.forEach((question) => {
        const transformedQuestion = resetQuestionFeatures(
          question,
          nodes,
          surveyConfig,
        );
        newQuestionnaireErrors = updateQuestionnaireErrors({
          newQuestionnaireErrors,
          nodeErrors: transformedQuestion.errors,
          nodeRefId: transformedQuestion.refId,
          nodeType: transformedQuestion.type,
        });
        newGroupQuestions.push(transformedQuestion);
      });
      group.questions = newGroupQuestions;

      newNodes.push(group);
    } else {
      const transformedQuestion = resetQuestionFeatures(
        node,
        nodes,
        surveyConfig,
      );
      newQuestionnaireErrors = updateQuestionnaireErrors({
        newQuestionnaireErrors,
        nodeErrors: transformedQuestion.errors,
        nodeRefId: transformedQuestion.refId,
        nodeType: transformedQuestion.type,
      });
      newNodes.push(transformedQuestion);
    }
  });
  return { newNodes, newQuestionnaireErrors };
}

// we need to remove piping on questions and carry forward settings from all questions that contain them
function resetQuestionFeatures(question, allNodes, surveyConfig) {
  let transformedQuestion = {
    ...question,
  };

  if (!question.isScreening) {
    transformedQuestion = removeAllMentionsFromNode(
      transformedQuestion,
      replaceRecallQuestionTagsInHtmlWithText,
    );
  }

  if (question.carryForward && question.carryForward.length > 0) {
    transformedQuestion = removeAllCarryForward(transformedQuestion);
  }

  let groupNode;
  if (question.groupRefId) {
    groupNode = getNodeByRefId(allNodes, question.groupRefId);
  }

  // we pass {} for the previousNodesPerNode/screeningQuestions parameter as we do not need it after we removed all carryForward settings
  transformedQuestion.errors = getNodeErrors({
    node: transformedQuestion,
    nodes: allNodes,
    previousNodesPerNode: {},
    answersWithLogicFromOutcomes: [],
    answersWithLogicFromOutcomesHide: [],
    screeningQuestions: [],
    groupNode,
    surveyConfig,
  });

  return transformedQuestion;
}

export function resetADL({ questionnaireLogic, questions }) {
  try {
    const defaultQl = defaultQlService.buildDefaultQlWithScreening(
      questionnaireLogic.ql,
      getNonScreeningQuestionsOrdered(questions),
    );

    return updateState({
      ql: defaultQl,
      questionnaireLogic,
    });
  } catch (error) {
    console.error('Error while resetADL', error);
    return Object.assign({}, questionnaireLogic, { error });
  }
}

export function getNonDefaultStatementsByQuestionRefId(state, questionRefId) {
  const nodeStatements = get(state, 'questionnaireLogic.ql.nodeStatements', []);
  const relevantStatements = get(
    find(nodeStatements, ['nodeId', questionRefId]),
    'statements',
    [],
  );
  const nonDefaultStatement = find(relevantStatements, ({ rule }) => rule);
  return nonDefaultStatement;
}

export function getDefaultStatementByQuestionRefId(state, questionRefId) {
  const nodeStatements = get(state, 'questionnaireLogic.ql.nodeStatements', []);
  const relevantStatements = get(
    find(nodeStatements, ['nodeId', questionRefId]),
    'statements',
    [],
  );
  const defaultStatement = find(relevantStatements, ({ rule }) => !rule);
  return defaultStatement;
}

export function addScreeningQuestionToQuestionnaireActionHandler(
  state,
  action,
) {
  // TODO: do we need to update the disqualificationLogic in this case?
  const newScreeningQuestions = getScreeningQuestionsAfterAdd(
    state.surveyDetailsExtended.screeningQuestions,
    action.payload,
  );

  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      screeningQuestions: newScreeningQuestions,
    },
  });
}

export function addMultipleScreeningQuestionsToQuestionnaire(state, action) {
  const { questions, originQuestionRefId } = action.payload;
  let newScreeningQuestions = state.surveyDetailsExtended.screeningQuestions;
  questions.forEach((question) => {
    newScreeningQuestions = getScreeningQuestionsAfterAdd(
      newScreeningQuestions,
      {
        newScreeningQuestion: question,
        insertAfterQuestionRefId: originQuestionRefId,
      },
    );
  });

  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      screeningQuestions: newScreeningQuestions,
    },
  });
}

export function removeScreeningQuestionFromQuestionnaireActionHandler(
  state,
  action,
) {
  const {
    questionnaireEditVersion,
    screeningQuestions,
    questions: nodes = [],
  } = state.surveyDetailsExtended;
  const removedQuestionRefId = action.payload.refId;
  let newQuestionnaireLogic = state.questionnaireLogic;

  newQuestionnaireLogic = updateQlAfterNodeRemove({
    nodeId: removedQuestionRefId,
    questionnaireLogic: state.questionnaireLogic,
    allNodes: [...screeningQuestions, ...nodes],
    shouldUpdateVersion: true,
  });

  let newDisqualificationLogic = state.disqualificationLogic;
  newDisqualificationLogic = updateQlAfterNodeRemove({
    nodeId: removedQuestionRefId,
    questionnaireLogic: state.disqualificationLogic,
    allNodes: [...screeningQuestions, ...nodes],
    shouldUpdateVersion: true,
  });

  const newScreeningQuestions = getScreeningQuestionsAfterRemove(
    screeningQuestions,
    removedQuestionRefId,
  );

  let newNodes = getNodesOnRemove(nodes, removedQuestionRefId);
  const isCarriedForwardedNode = isQuestionCarryForwarded(
    removedQuestionRefId,
    nodes,
  );

  if (isCarriedForwardedNode) {
    newNodes = removeMentionsOfRemovedAnswers(nodes, newNodes);
  }

  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: newNodes,
      screeningQuestions: newScreeningQuestions,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
    questionnaireLogic: newQuestionnaireLogic,
    disqualificationLogic: newDisqualificationLogic,
  });
}

export function removeAllScreeningQuestionFromQuestionnaireActionHandler(
  state,
) {
  const { screeningQuestions } = state.surveyDetailsExtended;
  let newState = state;
  screeningQuestions.forEach((screeningQuestion) => {
    newState = removeScreeningQuestionFromQuestionnaireActionHandler(newState, {
      payload: { refId: screeningQuestion.refId },
    });
  });
  return newState;
}

export function updateScreeningQuestionInQuestionnaireActionHandler(
  state,
  action,
) {
  const { question: updatedNode } = action.payload;
  const previousNodes = get(state, 'surveyDetailsExtended.questions', []);
  const screeningQuestions = get(
    state,
    'surveyDetailsExtended.screeningQuestions',
    [],
  );

  const questionnaireEditVersion = get(
    state,
    'surveyDetailsExtended.questionnaireEditVersion',
  );

  const updatedQuestion = cloneDeep(updatedNode);
  updatedQuestion.answers = filter(
    updatedQuestion.answers,
    ({ body, media, id }) => body || id || media,
  );
  const previousNode = find(screeningQuestions, [
    'refId',
    updatedQuestion.refId,
  ]);
  const newScreeningQuestions = getScreeningQuestionsAfterUpdate(
    screeningQuestions,
    updatedQuestion,
  );
  const isCarriedForwardedNode = isQuestionCarryForwarded(
    updatedQuestion.refId,
    previousNodes,
  );
  let updatedNodes = previousNodes;
  if (isCarriedForwardedNode) {
    updatedNodes = updateRelevantCarriedQuestions({
      questions: updatedNodes,
      sourceQuestion: updatedQuestion,
    });
    updatedNodes = removeMentionsOfRemovedAnswers(previousNodes, updatedNodes);
  }
  const stateToUpdateWithoutErrors = cloneDeep(state);
  stateToUpdateWithoutErrors.surveyDetailsExtended.questions = updatedNodes;
  stateToUpdateWithoutErrors.surveyDetailsExtended.questionnaireEditVersion =
    questionnaireEditVersion + 1;

  const newQuestionnaireLogic = updateQlAfterScreeningUpdate({
    state: stateToUpdateWithoutErrors,
    screeningQuestion: updatedQuestion,
    previousScreeningQuestion: previousNode,
    previousQuestions: previousNodes,
    targetLogic: 'questionnaireLogic',
  });

  let newDisqualificationLogic = state.disqualificationLogic;
  const shouldUpdateDisqualificationLogic =
    state.disqualificationLogic.version > -1;

  if (shouldUpdateDisqualificationLogic) {
    newDisqualificationLogic = updateQlAfterScreeningUpdate({
      state: stateToUpdateWithoutErrors,
      screeningQuestion: updatedQuestion,
      previousScreeningQuestion: previousNode,
      previousQuestions: previousNodes,
      targetLogic: 'disqualificationLogic',
    });
  }

  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      screeningQuestions: newScreeningQuestions,
      questionnaireEditVersion: questionnaireEditVersion + 1,
      questions: updatedNodes,
    },
    questionnaireLogic: newQuestionnaireLogic,
    disqualificationLogic: newDisqualificationLogic,
  });
}

export function abAddConceptActionHandler(state, action) {
  const { surveyDetailsExtended = {} } = state;
  const { questionnaireEditVersion } = surveyDetailsExtended;
  const { refId } = action.payload;
  const { questions: nodes } = surveyDetailsExtended;
  let globalErrors = state.errors;

  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: nodes.map((node) => {
        if (node.refId === refId) {
          let newNode = {
            ...node,
            concepts: [...node.concepts, getEmptyConcept()],
            attributes: [
              ...node.attributes.map((attribute) => ({
                ...attribute,
                values: [...attribute.values, getEmptyAttributeValue()],
              })),
            ],
          };

          ({ newNode, globalErrors } = attachErrorsOnNode({
            node: newNode,
            allNodes: nodes,
            globalErrors,
            previousNodesPerNode: get(
              state,
              'questionnaireLogic.previousNodesPerNode',
              {},
            ),
          }));

          return newNode;
        }

        return node;
      }),
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
    errors: globalErrors,
  });
}

export function abUpdateConceptActionHandler(state, action) {
  const { surveyDetailsExtended = {} } = state;
  const { questionnaireEditVersion } = surveyDetailsExtended;
  const { refId, conceptRefId, ...restPayload } = action.payload;
  const { questions: nodes } = surveyDetailsExtended;
  let globalErrors = state.errors;

  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: nodes.map((node) => {
        if (node.refId === refId) {
          let newNode = {
            ...node,
            concepts: node.concepts.map((concept) => ({
              ...concept,
              ...(conceptRefId === concept.refId ? restPayload : {}),
            })),
          };

          ({ newNode, globalErrors } = attachErrorsOnNode({
            node: newNode,
            allNodes: nodes,
            globalErrors,
            previousNodesPerNode: get(
              state,
              'questionnaireLogic.previousNodesPerNode',
              {},
            ),
          }));

          return newNode;
        }

        return node;
      }),
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
    errors: globalErrors,
  });
}

export function groupLoopAndMergeUpdateGroupActionHandler(
  state,
  { payload: { updatedGroup } },
) {
  const questionnaireEditVersion = get(
    state,
    'surveyDetailsExtended.questionnaireEditVersion',
    0,
  );
  const nodes = get(state, 'surveyDetailsExtended.questions', []);
  const screeningQuestions = get(
    state,
    'surveyDetailsExtended.screeningQuestions',
    [],
  );
  const previousNode = nodes.find((q) => q.refId === updatedGroup.refId);
  const updatedNodes = nodes.map((node) => {
    if (node.refId === updatedGroup.refId) {
      return updatedGroup;
    }
    return node;
  });

  let newQl = updateQlQuestionnaire({
    nodes: updatedNodes,
    ql: state.questionnaireLogic.ql,
  });

  if (
    isLoopAndMergeGroup(updatedGroup.type) &&
    isLoopAndMergeGroup(previousNode.type)
  ) {
    const { concepts: previousConcepts } = previousNode;
    const { concepts } = updatedGroup;

    const removedConcepts = differenceBy(previousConcepts, concepts, 'refId');
    removedConcepts.forEach(({ refId: removedConceptRefId }) => {
      newQl = questionnaireLogicService.removeConditionsWithValue(
        newQl,
        removedConceptRefId,
        QuestionnaireConditionContext.Concept,
      );
    });
  }

  const newQuestionnaireLogic = updateState({
    ql: newQl,
    questionnaireLogic: state.questionnaireLogic,
  });

  const logicKey = getLogicKeyFromMode(LOGIC_MODES.PLAIN);
  const { previousNodesPerNode } = newQuestionnaireLogic;
  const { errors: globalErrors } = state;

  const { questions: updatedQuestions, questionnaireErrors } =
    getRevalidatedQuestionsAndGlobalErrors({
      questions: updatedNodes,
      screeningQuestions,
      previousNodesPerNode: mergePreviousNodesPerNode(
        state,
        logicKey,
        previousNodesPerNode,
      ),
      globalErrors,
      filter: ({ type }) => type === GROUP_LOOP_MERGE,
      surveyConfig: getSurveyConfigForValidate(state),
    });

  return Object.assign({}, state, {
    errors: questionnaireErrors,
    questionnaireLogic: newQuestionnaireLogic,
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: updatedQuestions,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
  });
}

export function groupUpdateAttributeActionHandler(state, action) {
  const { surveyDetailsExtended = {} } = state;
  const {
    refId,
    attributeRefId,
    groupType,
    removedAttributeValues,
    pristineValues,
    ...restPayload
  } = action.payload;
  const { questions: nodes, questionnaireEditVersion } = surveyDetailsExtended;
  let globalErrors = state.errors;
  const { values, previousValues } = restPayload;

  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: nodes.map((node) => {
        if (node.refId === refId) {
          let newNode = {
            ...cloneDeep(node),
            pristineValues: pristineValues || node.pristineValues,
            attributes: node.attributes.map((attribute) => {
              if (attribute.refId === attributeRefId) {
                return {
                  ...attribute,
                  ...restPayload,
                };
              }

              return attribute;
            }),
          };

          if (isConjointOrMaxDiffGroup(groupType) && removedAttributeValues) {
            newNode.properties = updateConjointOrMaxDiffPropertiesOnRemove({
              properties: node.properties,
              attributeValueRefIds: removedAttributeValues,
            });
            newNode.properties.doeStructureVersion += 1;
          }
          if (
            isConjointOrMaxDiffGroup(groupType) &&
            previousValues &&
            values &&
            // either removed or added
            previousValues.length !== values.length
          ) {
            newNode.properties.doeStructureVersion += 1;
          }

          ({ newNode, globalErrors } = attachErrorsOnNode({
            node: newNode,
            allNodes: nodes,
            globalErrors,
            previousNodesPerNode: get(
              state,
              'questionnaireLogic.previousNodesPerNode',
              {},
            ),
          }));

          return newNode;
        }

        return node;
      }),
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
    errors: globalErrors,
  });
}

export function groupAddAttributeActionHandler(state, action) {
  const { surveyDetailsExtended = {} } = state;
  const { questionnaireEditVersion } = surveyDetailsExtended;
  const { refId, groupType, ...restPayload } = action.payload;
  const { questions: nodes } = surveyDetailsExtended;
  let globalErrors = state.errors;

  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: nodes.map((node) => {
        if (node.refId === refId) {
          let newAttribute;

          if (isLoopAndMergeGroup(groupType)) {
            const { fromQuestionRefId } = restPayload;
            const additionalData = {
              name: getLoopAndMergeAttributeName(node.attributes.length),
            };
            if (fromQuestionRefId) {
              //
            }
            newAttribute = getEmptyAttribute(
              {
                groupType: GROUP_LOOP_MERGE,
                concepts: node.concepts && node.concepts.length,
              },
              additionalData,
            );
          } else {
            newAttribute = getEmptyAttribute(
              { groupType, concepts: node.concepts && node.concepts.length },
              restPayload,
            );
          }

          let newNode = {
            ...cloneDeep(node),
            attributes: [...node.attributes, newAttribute],
            questions: isAbGroup(groupType)
              ? node.questions.map((subNode) => ({
                  ...subNode,
                  selectedAttributes: [
                    ...subNode.selectedAttributes,
                    {
                      refId: newAttribute.refId,
                      selected: true,
                    },
                  ],
                }))
              : node.questions,
          };
          if (isConjointOrMaxDiffGroup(groupType)) {
            newNode.properties.doeStructureVersion += 1;
          }

          ({ newNode, globalErrors } = attachErrorsOnNode({
            node: newNode,
            allNodes: nodes,
            globalErrors,
            previousNodesPerNode: get(
              state,
              'questionnaireLogic.previousNodesPerNode',
              {},
            ),
          }));

          return newNode;
        }

        return node;
      }),
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
    errors: globalErrors,
  });
}

export function groupAddConceptActionHandler(state, action) {
  const { surveyDetailsExtended = {} } = state;
  const { questionnaireEditVersion } = surveyDetailsExtended;
  const { refId } = action.payload;
  const { questions: nodes } = surveyDetailsExtended;
  let globalErrors = state.errors;

  let loopAndMergeNode;
  const conditionalUpdates = {};

  const updatedQuestions = nodes.map((node) => {
    if (node.refId === refId) {
      const concept = getLoopAndMergeConceptWithName(node.concepts.length);

      const attributes = node.attributes.map((attribute) => ({
        ...attribute,
        values: [...attribute.values, getEmptyAttributeValue()],
      }));

      const updatedConcepts = [...node.concepts, concept];

      let newNode = {
        ...cloneDeep(node),
        attributes,
        concepts: updatedConcepts,
      };

      if (isLoopAndMergeGroup(node.type)) {
        if (!node.properties.userDefinedRepetitions) {
          newNode.properties = {
            ...newNode.properties,
            repetitions: updatedConcepts.length,
          };
        }
        loopAndMergeNode = newNode;
      }

      ({ newNode, globalErrors } = attachErrorsOnNode({
        node: newNode,
        allNodes: nodes,
        globalErrors,
        previousNodesPerNode: get(
          state,
          'questionnaireLogic.previousNodesPerNode',
          {},
        ),
      }));

      return newNode;
    }

    return node;
  });

  if (loopAndMergeNode) {
    const newQuestionnaireLogic = updateState({
      ql: questionnaireLogicService.updateGroupNode(
        state.questionnaireLogic.ql,
        refId,
        {
          nodes: getQlSubNodes(loopAndMergeNode),
          repetitions: loopAndMergeNode.properties.repetitions,
          type: loopAndMergeNode.groupType,
        },
      ),
      questionnaireLogic: state.questionnaireLogic,
    });

    conditionalUpdates.questionnaireLogic = newQuestionnaireLogic;
  }

  return Object.assign({}, state, {
    ...conditionalUpdates,
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: updatedQuestions,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
    errors: globalErrors,
  });
}

export function abUpdatePropertiesActionHandler(state, action) {
  const { surveyDetailsExtended = {} } = state;
  const { questionnaireEditVersion } = surveyDetailsExtended;
  const { refId, properties } = action.payload;
  const { questions: nodes } = surveyDetailsExtended;

  const { newNodes, globalErrors } = updateGroupProperties({
    refId,
    nodes,
    properties,
    errors: state.errors,
    previousNodesPerNode: get(
      state,
      'questionnaireLogic.previousNodesPerNode',
      {},
    ),
  });

  const newQl = updateQlQuestionnaire({
    nodes: newNodes,
    ql: state.questionnaireLogic.ql,
  });

  const newQuestionnaireLogic = updateState({
    ql: newQl,
    questionnaireLogic: state.questionnaireLogic,
  });

  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: newNodes,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
    errors: globalErrors,
    questionnaireLogic: newQuestionnaireLogic,
  });
}

export function loopAndMergeUpdatePropertiesActionHandler(state, action) {
  const { surveyDetailsExtended = {} } = state;
  const { questionnaireEditVersion } = surveyDetailsExtended;
  const { refId, properties } = action.payload;
  const { questions: nodes } = surveyDetailsExtended;

  const { newNodes, globalErrors } = updateGroupProperties({
    refId,
    nodes,
    properties,
    errors: state.errors,
    previousNodesPerNode: get(
      state,
      'questionnaireLogic.previousNodesPerNode',
      {},
    ),
  });

  const newQl = updateQlQuestionnaire({
    nodes: newNodes,
    ql: state.questionnaireLogic.ql,
  });

  const newQuestionnaireLogic = updateState({
    ql: newQl,
    questionnaireLogic: state.questionnaireLogic,
  });

  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: newNodes,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
    errors: globalErrors,
    questionnaireLogic: newQuestionnaireLogic,
  });
}

export function groupUpdateAttributeValueActionHandler(state, action) {
  const { surveyDetailsExtended = {} } = state;
  const { questionnaireEditVersion } = surveyDetailsExtended;
  const { questions: nodes } = surveyDetailsExtended;
  const { newNodes, globalErrors } = updateGroupPropertyValues(
    nodes,
    action.payload,
    state.errors,
  );

  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: newNodes,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
    errors: globalErrors,
  });
}

export function abRemoveConceptActionHandler(state, action) {
  const { surveyDetailsExtended = {} } = state;
  const { questionnaireEditVersion } = surveyDetailsExtended;
  const { refId, conceptRefId } = action.payload;
  const { questions: nodes } = surveyDetailsExtended;
  let globalErrors = state.errors;

  const shouldUpdateDisqualificationLogic =
    state.disqualificationLogic.version > -1;
  const disqualificationUpdates = {};
  if (shouldUpdateDisqualificationLogic) {
    const newQl = questionnaireLogicService.removeConditionsWithValue(
      state.disqualificationLogic.ql,
      conceptRefId,
      QuestionnaireConditionContext.Concept,
    );
    disqualificationUpdates.disqualificationLogic = updateState({
      ql: newQl,
      questionnaireLogic: state.disqualificationLogic,
    });
  }

  const newQl = questionnaireLogicService.removeConditionsWithValue(
    state.questionnaireLogic.ql,
    conceptRefId,
    QuestionnaireConditionContext.Concept,
  );
  const newQuestionnaireLogic = updateState({
    ql: newQl,
    questionnaireLogic: state.questionnaireLogic,
  });

  const newNodes = nodes.map((node) => {
    if (node.refId === refId) {
      const conceptIndex = node.concepts.findIndex(
        (concept) => concept.refId === conceptRefId,
      );

      let newNode = {
        ...node,
        concepts: node.concepts.filter(
          (concept) => concept.refId !== conceptRefId,
        ),
        attributes: node.attributes.map((attribute) => ({
          ...attribute,
          values: attribute.values.filter(
            (_, valueIndex) => valueIndex !== conceptIndex,
          ),
        })),
      };

      ({ newNode, globalErrors } = attachErrorsOnNode({
        node: newNode,
        allNodes: nodes,
        globalErrors,
        previousNodesPerNode: newQuestionnaireLogic.previousNodesPerNode,
      }));

      return newNode;
    }

    return node;
  });
  return Object.assign({}, state, {
    questionnaireLogic: newQuestionnaireLogic,
    ...disqualificationUpdates,
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: newNodes,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
    errors: globalErrors,
  });
}

function getQuestionsForGroupWithoutAttrRefs(groupNode, attributeRefId) {
  const checkForMention = hasAttributeMentionOnRefId(attributeRefId);

  return groupNode.questions.map((node) => {
    if (hasNodeMentions(node, checkForMention)) {
      return removeAllMentionsFromNode(
        node,
        removeAttrRefIdMentionsFn(attributeRefId),
      );
    }

    return node;
  });
}

export function groupRemoveAttributeActionHandler(state, action) {
  const { surveyDetailsExtended = {} } = state;
  const { refId, attributeRefId, groupType } = action.payload;
  const { questions: nodes, questionnaireEditVersion } = surveyDetailsExtended;
  let globalErrors = state.errors;
  let attributeValueRefIdsToRemoved = [];

  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: nodes.map((node) => {
        if (node.refId === refId) {
          const newAttributes = node.attributes.filter((attribute) => {
            if (attribute.refId === attributeRefId) {
              attributeValueRefIdsToRemoved = attribute.values.map(
                (attributeValue) => attributeValue.refId,
              );
              return false;
            }

            return true;
          });
          let newNode = {
            ...cloneDeep(node),
            attributes: newAttributes,
            questions: isAbGroup(groupType)
              ? node.questions.map((subNode) => ({
                  ...subNode,
                  selectedAttributes: subNode.selectedAttributes.filter(
                    (attribute) => attribute.refId !== attributeRefId,
                  ),
                }))
              : node.questions,
          };

          if (isConjointOrMaxDiffGroup(groupType)) {
            newNode.properties = updateConjointOrMaxDiffPropertiesOnRemove({
              properties: node.properties,
              attributeValueRefIds: attributeValueRefIdsToRemoved,
              cleanExcludedCombinations: newAttributes.length < 2,
            });
            newNode.properties.doeStructureVersion += 1;
          }

          if (isLoopAndMergeGroup(groupType)) {
            // handle child question mentions for variables
            newNode.questions = getQuestionsForGroupWithoutAttrRefs(
              newNode,
              attributeRefId,
            );
          }

          ({ newNode, globalErrors } = attachErrorsOnNode({
            node: newNode,
            allNodes: nodes,
            globalErrors,
            previousNodesPerNode: get(
              state,
              'questionnaireLogic.previousNodesPerNode',
              {},
            ),
          }));

          return newNode;
        }

        return node;
      }),
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
    errors: globalErrors,
  });
}

export function groupReOrderLoopAndMergeConcept(state, action) {
  const { surveyDetailsExtended = {} } = state;
  const { questionnaireEditVersion, questions: nodes } = surveyDetailsExtended;
  const { refId, conceptRefId, direction } = action.payload;
  let didUpdate = false;

  const updatedQuestions = nodes.map((node) => {
    if (node.refId === refId) {
      const { concepts } = node;
      const conceptIndex = concepts.findIndex(
        (concept) => concept.refId === conceptRefId,
      );
      let newIndex;
      if (direction === DIRECTION.UP && conceptIndex > 0) {
        newIndex = conceptIndex - 1;
      } else if (
        direction === DIRECTION.DOWN &&
        conceptIndex < concepts.length - 1
      ) {
        newIndex = conceptIndex + 1;
      } else {
        newIndex = conceptIndex;
      }

      if (newIndex === conceptIndex) {
        return node;
      }
      didUpdate = true;
      const updatedConcepts = arrayMove(concepts, conceptIndex, newIndex).map(
        (concept, index) => ({
          ...concept,
          name: getLoopAndMergeConceptName(index),
        }),
      );

      const newAttributes = node.attributes.map((attribute) => ({
        ...attribute,
        values: arrayMove(attribute.values, conceptIndex, newIndex),
      }));

      const updatedLoopAndMergeGroup = {
        ...cloneDeep(node),
        attributes: newAttributes,
        concepts: updatedConcepts,
      };

      return updatedLoopAndMergeGroup;
    }

    return node;
  });

  if (!didUpdate) {
    return state;
  }

  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: updatedQuestions,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
  });
}

export function groupRemoveConceptLoopAndMergeActionHandler(state, action) {
  const { surveyDetailsExtended = {} } = state;
  const {
    // conceptRefId,
    refId,
    conceptIndex,
  } = action.payload;
  const { questions: nodes, questionnaireEditVersion } = surveyDetailsExtended;
  let globalErrors = state.errors;

  const shouldUpdateDisqualificationLogic =
    state.disqualificationLogic.version > -1;
  const disqualificationUpdates = {};
  let loopAndMergeNode;
  const conditionalUpdates = {};
  let removedConceptRefId;

  const updatedQuestions = nodes.map((node) => {
    if (node.refId === refId) {
      removedConceptRefId = node.concepts[conceptIndex]?.refId;
      const newConcepts = node.concepts
        .filter((_, index) => index !== conceptIndex)
        .map((concept, index) => ({
          ...concept,
          name: getLoopAndMergeConceptName(index),
        }));

      const newAttributes = node.attributes.map((attribute) => ({
        ...attribute,
        values: attribute.values.filter(
          (_, valueIndex) => valueIndex !== conceptIndex,
        ),
      }));

      let newNode = {
        ...cloneDeep(node),
        attributes: newAttributes,
        concepts: newConcepts,
      };

      if (!newNode.properties.userDefinedRepetitions) {
        newNode.properties.repetitions = newConcepts.length;
      }

      loopAndMergeNode = newNode;

      ({ newNode, globalErrors } = attachErrorsOnNode({
        node: newNode,
        allNodes: nodes,
        globalErrors,
        previousNodesPerNode: get(
          state,
          'questionnaireLogic.previousNodesPerNode',
          {},
        ),
      }));

      return newNode;
    }

    return node;
  });

  if (loopAndMergeNode) {
    const newQl = questionnaireLogicService.removeConditionsWithValue(
      state.questionnaireLogic.ql,
      removedConceptRefId,
      QuestionnaireConditionContext.Concept,
    );

    if (shouldUpdateDisqualificationLogic) {
      const newDisqualificationQl =
        questionnaireLogicService.removeConditionsWithValue(
          state.disqualificationLogic.ql,
          removedConceptRefId,
          QuestionnaireConditionContext.Concept,
        );
      disqualificationUpdates.disqualificationLogic = updateState({
        ql: newDisqualificationQl,
        questionnaireLogic: state.disqualificationLogic,
      });
    }

    const newQuestionnaireLogic = updateState({
      ql: questionnaireLogicService.updateGroupNode(newQl, refId, {
        nodes: getQlSubNodes(loopAndMergeNode),
        repetitions: loopAndMergeNode.properties.repetitions,
        type: loopAndMergeNode.groupType,
      }),
      questionnaireLogic: state.questionnaireLogic,
    });

    conditionalUpdates.questionnaireLogic = newQuestionnaireLogic;
  }

  return Object.assign({}, state, {
    ...conditionalUpdates,
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: updatedQuestions,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
    errors: globalErrors,
  });
}

export function abAddDefaultNodesActionHandler(state, action) {
  const { surveyDetailsExtended = {}, questionnaireLogic } = state;
  const { questionnaireEditVersion } = surveyDetailsExtended;
  const { refId, type } = action.payload;
  const { questions: nodes, screeningQuestions } = surveyDetailsExtended;

  let newNode;
  let additionalNodes = [];

  const newNodes = nodes.map((node) => {
    if (node.refId === refId) {
      additionalNodes = getPredefinedABQuestions(
        refId,
        type,
        getPreSelectedAttributes(node.attributes),
      );

      newNode = {
        ...node,
        questions: [...node.questions, ...additionalNodes],
      };

      return newNode;
    }

    return node;
  });

  let { ql: newQl } = questionnaireLogic;
  if (additionalNodes) {
    additionalNodes.forEach((addedNode) => {
      const transformedAddedNode = transformNodeToQlQuestionnaireNode({
        node: addedNode,
      });
      newQl = questionnaireLogicService.addNode(
        newQl,
        transformedAddedNode,
        undefined,
        undefined,
        undefined,
        refId,
      );
    });
  }

  const newQuestionnaireLogic = updateState({
    ql: newQl,
    questionnaireLogic,
  });
  const {
    previousNodesPerNode,
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
  } = newQuestionnaireLogic;

  const nodeErrors = getNodeErrors({
    node: newNode,
    nodes: newNodes,
    screeningQuestions,
    previousNodesPerNode,
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
  });

  const newGlobalErrors = updateQuestionnaireErrors({
    globalErrors: state.errors,
    nodeRefId: newNode.refId,
    groupRefId: newNode.groupRefId,
    nodeErrors,
    nodeType: newNode.type,
  });

  return Object.assign({}, state, {
    questionnaireLogic: newQuestionnaireLogic,
    errors: newGlobalErrors,
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: newNodes,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
  });
}

// REMOVE_QUESTION_FROM_QUESTIONNAIRE
export function removeQuestionFromQuestionnaireActionHandler(state, action) {
  const {
    payload: { refId, groupRefId },
  } = action;
  const { surveyDetailsExtended } = state;
  const {
    questions: nodes = [],
    screeningQuestions,
    questionnaireEditVersion,
  } = surveyDetailsExtended;

  const targetLogic = getLogicKeyFromMode(LOGIC_MODES.PLAIN);

  const updatedNodes = getNodesOnRemove(nodes, refId, groupRefId);

  let newDisqualificationLogic = state.disqualificationLogic;
  const shouldUpdateDisqualificationLogic =
    state.disqualificationLogic.version > -1; // TODO RES-4563: is this the best check?

  const newQuestionnaireLogic = updateQlAfterNodeRemove({
    nodeId: refId,
    questionnaireLogic: state[targetLogic],
    allNodes: nodes,
  });
  const {
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
  } = newQuestionnaireLogic;

  if (shouldUpdateDisqualificationLogic) {
    newDisqualificationLogic = updateQlAfterNodeRemove({
      nodeId: refId,
      questionnaireLogic: state.disqualificationLogic,
      allNodes: nodes,
      shouldUpdateVersion: true,
    });
  }

  const { newNodes, globalErrors } = getRevalidatedNodesAndGlobalErrors({
    nodes: updatedNodes,
    errors: state.errors,
    previousNodesPerNode: mergePreviousNodesPerNode(
      state,
      targetLogic,
      newQuestionnaireLogic.previousNodesPerNode,
    ),
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
    screeningQuestions,
    surveyConfig: getSurveyConfigForValidate(state),
  });

  // we reset the shuffling of the questionnaire because when you remove
  // every node, if the questionnaire randomization is active you are only
  // allowed to create a group, not a question. And this is a little confusing
  // because we are not showing anywhere _why_ you can only add a group (the sidebar is hidden)
  const shouldResetQuestionnaireShuffling = newNodes.length === 0;

  const newState = saveCheckpointHandler(state, true);
  const updatedState = Object.assign({}, newState, {
    [targetLogic]: newQuestionnaireLogic,
    surveyDetailsExtended: {
      ...newState.surveyDetailsExtended,
      questions: newNodes,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
    questionGroupShuffle: shouldResetQuestionnaireShuffling
      ? SHUFFLE_NONE
      : newState.questionGroupShuffle,
    errors: attachAIGlobalErrors(globalErrors, newNodes).filter(
      (error) => !(error.questionRefId === refId || error.groupRefId === refId),
    ),
    disqualificationLogic: newDisqualificationLogic,
  });

  return validateQuotaAndUpdateState(updatedState);
}

export function vanWestendorpToggleLikelihoodNodesActionHandler(state, action) {
  const { surveyDetailsExtended: initialDetails = {} } = state;
  const { questions: initialNodes } = initialDetails;
  const { refId, enableLikelihood = true } = action.payload;

  let newState = state;
  let newNode;

  let vanWestendorpGroup = find(initialNodes, ['refId', refId]);

  if (!vanWestendorpGroup) {
    vanWestendorpGroup = find(
      initialNodes,
      (node) =>
        isVanWestendorpGroup(node.type) &&
        find(node.questions, ['refId', refId]),
    );
  }

  if (!vanWestendorpGroup?.questions) {
    return state;
  }

  const vanWestendorpQuestion = find(vanWestendorpGroup.questions, [
    'type',
    QUESTION_VAN_WESTENDORP,
  ]);

  if (!vanWestendorpQuestion) {
    return state;
  }

  if (!enableLikelihood) {
    const { relatedQuestions = [] } = vanWestendorpQuestion;

    forEach(relatedQuestions, (relatedQuestionRefId) => {
      newState = removeQuestionFromQuestionnaireActionHandler(newState, {
        payload: {
          refId: relatedQuestionRefId,
          groupRefId: vanWestendorpGroup.refId,
        },
      });
    });
  }

  const { surveyDetailsExtended } = newState;
  const {
    questionnaireEditVersion,
    questions: nodes,
    screeningQuestions,
  } = surveyDetailsExtended;

  const currentGroup =
    find(nodes, ['refId', vanWestendorpGroup.refId]) || vanWestendorpGroup;
  const currentVanWestendorpQuestion =
    find(currentGroup.questions, ['type', QUESTION_VAN_WESTENDORP]) ||
    vanWestendorpQuestion;

  let additionalRelatedQuestions = [];

  const newNodes = nodes.map((node) => {
    if (node.refId === vanWestendorpGroup.refId) {
      if (enableLikelihood) {
        if ((currentVanWestendorpQuestion.relatedQuestions || []).length > 0) {
          newNode = node;
          return node;
        }

        const { cheap, expensive } = getVanWestendorpRecallAnswers(
          currentVanWestendorpQuestion,
        );

        if (!cheap || !expensive) {
          return node;
        }

        additionalRelatedQuestions = getPredefinedVanWestendorpQuestions(
          vanWestendorpGroup.refId,
          PREDEFINED_LIKELIHOOD,
          currentVanWestendorpQuestion.selectedAttributes,
        ).map((relatedQuestion) => {
          let updatedQuestion = { ...relatedQuestion };

          // replace cheap subRefId
          let { newPrompt, newHtmlPrompt } = replaceMentionsRefIdWithOther({
            question: updatedQuestion,
            refId: 'vw',
            newRefId: currentVanWestendorpQuestion.refId,
            subRefId: 'cheap',
            newSubRefId: cheap.refId,
          });

          updatedQuestion = {
            ...updatedQuestion,
            prompt: newPrompt,
            htmlPrompt: newHtmlPrompt,
            hasPiping: hasPiping(newPrompt || newHtmlPrompt),
          };

          // replace expensive subRefId
          ({ newPrompt, newHtmlPrompt } = replaceMentionsRefIdWithOther({
            question: updatedQuestion,
            refId: 'vw',
            newRefId: currentVanWestendorpQuestion.refId,
            subRefId: 'expensive',
            newSubRefId: expensive.refId,
          }));

          return {
            ...updatedQuestion,
            prompt: newPrompt,
            htmlPrompt: newHtmlPrompt,
            hasPiping: hasPiping(newPrompt || newHtmlPrompt),
          };
        });

        const relatedQuestionRefIds = additionalRelatedQuestions.map(
          (likelihoodQuestion) => likelihoodQuestion.refId,
        );

        newNode = {
          ...node,
          questions: [
            ...node.questions.map((question) => {
              if (isVanWestendorpQuestionType(question.type)) {
                return { ...question, relatedQuestions: relatedQuestionRefIds };
              }
              return question;
            }),
            ...additionalRelatedQuestions,
          ],
        };

        return newNode;
      }
      newNode = {
        ...node,
        questions: [
          ...node.questions.map((subNode) => {
            if (isVanWestendorpQuestionType(subNode.type)) {
              return { ...subNode, relatedQuestions: [] };
            }
            return subNode;
          }),
        ],
      };
      return newNode;
    }

    return node;
  });

  if (!newNode) {
    return state;
  }

  const { questionnaireLogic = {} } = newState;

  let { ql: newQl } = questionnaireLogic;
  if (enableLikelihood && additionalRelatedQuestions.length > 0) {
    additionalRelatedQuestions.forEach((relatedQuestion) => {
      const transformedQuestion = transformNodeToQlQuestionnaireNode({
        node: relatedQuestion,
      });
      newQl = questionnaireLogicService.addNode(
        newQl,
        transformedQuestion,
        undefined,
        undefined,
        undefined,
        newNode.refId,
      );
    });
  }

  const newQuestionnaireLogic = updateState({
    ql: newQl,
    questionnaireLogic,
  });
  const {
    previousNodesPerNode,
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
  } = newQuestionnaireLogic;

  const nodeErrors = getNodeErrors({
    node: newNode,
    nodes: newNodes,
    screeningQuestions,
    previousNodesPerNode,
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
  });

  const newGlobalErrors = updateQuestionnaireErrors({
    globalErrors: newState.errors,
    nodeRefId: newNode.refId,
    groupRefId: newNode.groupRefId,
    nodeErrors,
    nodeType: newNode.type,
  });

  return Object.assign({}, newState, {
    questionnaireLogic: newQuestionnaireLogic,
    errors: newGlobalErrors,
    surveyDetailsExtended: {
      ...newState.surveyDetailsExtended,
      questions: newNodes,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
  });
}

export function vanWestendorpUpdateSettingsHandler(state, action) {
  const { surveyDetailsExtended = {} } = state;
  const { questionnaireEditVersion } = surveyDetailsExtended;
  const { refId, settings, partialPristineValues = {} } = action.payload;
  const { questions: nodes } = surveyDetailsExtended;

  let globalErrors = state.errors;

  const newQuestions = nodes.map((node) => {
    if (node.refId === refId) {
      let newNode = node;
      newNode = {
        ...newNode,
        pristineValues: {
          ...newNode.pristineValues,
          ...partialPristineValues,
        },
        questions: node.questions.map((question) => {
          if (isVanWestendorpQuestionType(question.type)) {
            return {
              ...question,
              matrix: {
                ...question.matrix,
                validation: {
                  vanWestendorpValidationDto: {
                    ...get(
                      question,
                      'matrix.validation.vanWestendorpValidationDto',
                      {},
                    ),
                    ...settings,
                  },
                },
              },
            };
          }
          return question;
        }),
      };

      if (globalErrors) {
        ({ newNode, globalErrors } = attachErrorsOnNode({
          node: newNode,
          allNodes: nodes,
          globalErrors,
          previousNodesPerNode: get(
            state,
            'questionnaireLogic.previousNodesPerNode',
            {},
          ),
        }));
      }

      return newNode;
    }

    return node;
  });

  return Object.assign({}, state, {
    errors: globalErrors,
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: newQuestions,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
  });
}

export function matrixOpenEndedUpdateSettingsHandler(state, action) {
  const { surveyDetailsExtended: initialDetails = {} } = state;
  const { questionnaireEditVersion } = initialDetails;
  const {
    refId,
    customSignEnabled,
    sign,
    signPlacement,
    allowedDecimals: decimals,
    decimalLimitEnabled,
    rangeLimitEnabled,
    min,
    max,
    gridType,
    total,
    isConstantSum = false, // TODO: RES-6958
  } = action.payload;

  const { questions: nodes } = initialDetails;

  let globalErrors = state.errors;
  const partialPristineValues = {};

  const updateNode = (node) => {
    let newNode = node;

    const openEndedConfigUpdates = !isNil(customSignEnabled)
      ? {
          sign: customSignEnabled ? sign : undefined,
          signPlacement: customSignEnabled
            ? signPlacement || MATRIX_OPEN_ENDED_SIGN_PLACEMENT.LEFT
            : MATRIX_OPEN_ENDED_SIGN_PLACEMENT.LEFT,
        }
      : {};

    const openEndedValidationUpdates = {};

    if (decimalLimitEnabled) {
      openEndedValidationUpdates.decimals = decimals;
    } else {
      openEndedValidationUpdates.decimals = undefined;
    }

    if (rangeLimitEnabled) {
      openEndedValidationUpdates.min = min;
      openEndedValidationUpdates.max = max;
      partialPristineValues.isPristineLimits =
        newNode.pristineValues.isPristineLimits &&
        !(!isNil(min) && !isNil(max));
    } else {
      openEndedValidationUpdates.min = undefined;
      openEndedValidationUpdates.max = undefined;
    }

    if (isConstantSum) {
      openEndedValidationUpdates.gridType = gridType;
      openEndedConfigUpdates.total = total;
    }

    newNode = {
      ...newNode,
      pristineValues: {
        ...newNode.pristineValues,
        ...partialPristineValues,
      },
      matrix: {
        ...node.matrix,
        openEndedConfig: {
          ...get(node, 'matrix.openEndedConfig', { refId: uuidv1() }),
          ...openEndedConfigUpdates,
        },
        validation: {
          openEndedValidation: {
            ...get(node, 'matrix.validation.openEndedValidation', {
              refId: uuidv1(),
            }),
            ...openEndedValidationUpdates,
          },
        },
      },
    };

    if (globalErrors) {
      ({ newNode, globalErrors } = attachErrorsOnNode({
        node: newNode,
        allNodes: nodes,
        globalErrors,
        previousNodesPerNode: get(
          state,
          'questionnaireLogic.previousNodesPerNode',
          {},
        ),
      }));

      return newNode;
    }

    return newNode;
  };

  const newQuestions = nodes.map((node) => {
    if (
      node.refId === refId &&
      [
        QUESTION_MATRIX_OPEN_ENDED,
        QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
        QUESTION_MATRIX_CONSTANT_SUM,
      ].includes(node.type)
    ) {
      return updateNode(node);
    }
    if (node.questions) {
      return {
        ...node,
        questions: node.questions.map((subNode) => {
          if (
            subNode.refId === refId &&
            [
              QUESTION_MATRIX_OPEN_ENDED,
              QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
              QUESTION_MATRIX_CONSTANT_SUM,
            ].includes(subNode.type)
          ) {
            return updateNode(subNode);
          }
          return subNode;
        }),
      };
    }

    return node;
  });

  return Object.assign({}, state, {
    errors: globalErrors,
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: newQuestions,
      questionnaireEditVersion: questionnaireEditVersion + 1,
    },
  });
}

export function restoreDefaultRevisionInState({
  state,
  questionnaire,
  isQuestionnairePreview,
}) {
  const newState = Object.assign({}, state, {
    errors: questionnaire.errors,
    questionnaireLogic: questionnaire.questionnaireLogic,
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      ...omit(questionnaire, QUESTIONNAIRE_DEFAULT_SUBSTATE_PATHS_TO_OMIT),
      isQuestionnairePreview,
    },
  });

  return newState;
}

export function attachHasPipingOnRevisionInState(state) {
  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: state.surveyDetailsExtended.questions.map((node) => {
        if (node.questions) {
          return {
            ...node,
            questions: node.questions.map(attachHasPipingOnNode),
          };
        }

        return attachHasPipingOnNode(node);
      }),
    },
  });
}

export function setDefaultRevisionInState(state) {
  const current = extractSubstateForQuestionnaire(state);

  const newState = Object.assign({}, state, {
    revisions: {
      ...state.revisions,
      questionnaireRevisions: {
        current,
      },
    },
  });

  return newState;
}

export function deleteHiddenVariableFromLogic(state, refId) {
  const variablesWithLogic = {
    variablesWithQuestionnaireLogic:
      state.questionnaireLogic.variablesWithLogic,
    variablesWithDisqualificationLogic:
      state.disqualificationLogic.variablesWithLogic,
  };
  const { hasVariableWithLogic, hasVariableWithDisqualificationLogic } =
    variableRefIdInLogic(variablesWithLogic, refId);
  const newState = { ...state };

  if (hasVariableWithLogic) {
    newState.questionnaireLogic = updateState({
      ql: questionnaireLogicService.removeVariableWithLogicById(
        newState.questionnaireLogic.ql,
        refId,
      ),
      questionnaireLogic: newState.questionnaireLogic,
      shouldUpdateVersion: false,
    });
  }

  if (hasVariableWithDisqualificationLogic) {
    newState.disqualificationLogic = updateState({
      ql: questionnaireLogicService.removeVariableWithLogicById(
        newState.disqualificationLogic.ql,
        refId,
      ),
      questionnaireLogic: newState.disqualificationLogic,
      shouldUpdateVersion: false,
    });
  }

  return newState;
}

export function deleteHiddenVariableValueFromLogic(state, refId) {
  const variablesWithLogic = {
    variablesWithQuestionnaireLogic:
      state.questionnaireLogic.variablesWithLogic,
    variablesWithDisqualificationLogic:
      state.disqualificationLogic.variablesWithLogic,
  };
  const {
    hasVariableValueWithLogic,
    hasVariableValueWithDisqualificationLogic,
  } = variableValueRefIdInLogic(variablesWithLogic, refId);
  const newState = { ...state };

  if (hasVariableValueWithLogic) {
    newState.questionnaireLogic = updateState({
      ql: questionnaireLogicService.removeVariableWithLogicByValue(
        newState.questionnaireLogic.ql,
        refId,
      ),
      questionnaireLogic: newState.questionnaireLogic,
      shouldUpdateVersion: false,
    });
  }

  if (hasVariableValueWithDisqualificationLogic) {
    newState.disqualificationLogic = updateState({
      ql: questionnaireLogicService.removeVariableWithLogicByValue(
        newState.disqualificationLogic.ql,
        refId,
      ),
      questionnaireLogic: newState.disqualificationLogic,
      shouldUpdateVersion: false,
    });
  }

  return newState;
}

export function cleanLogicFromTaxonomyProperties(
  state,
  taxonomyProperties = [],
) {
  if (taxonomyProperties.length === 0) {
    return state;
  }

  let newQl = state.questionnaireLogic.ql;
  taxonomyProperties.forEach((taxonomyProperty) => {
    newQl = questionnaireLogicService.removeConditionsWithProperty(
      newQl,
      taxonomyProperty,
      QuestionnaireConditionContext.Taxonomy,
    );
  });

  const questionnaireLogic = updateState({
    ql: newQl,
    questionnaireLogic: state.questionnaireLogic,
  });

  return Object.assign({}, state, {
    questionnaireLogic,
  });
}

export function addQuestionRefIdCorrection({
  refId,
  isScreening,
  screeningQuestions,
}) {
  const isScreeningQuestionRefId = screeningQuestions.some(
    (screeningQuestion) => screeningQuestion.refId === refId,
  );
  let updatedRefId = refId;
  if (isScreening && !isScreeningQuestionRefId) {
    updatedRefId = screeningQuestions.length
      ? screeningQuestions[screeningQuestions.length - 1].refId
      : undefined;
  }
  return updatedRefId;
}

export function updateGroupPropertiesHandler(
  payload,
  nodes,
  previousNodesPerNode,
) {
  const { refId, property, value, nodesToClearMentions = [] } = payload;

  return nodes.map((node) => {
    if (node.refId === refId) {
      const updatedNode = cloneDeep(node);
      updatedNode[property] = value;

      updatedNode.questions = updatedNode.questions.map((childNode) => {
        let newChildNode = childNode;

        if (nodesToClearMentions.length > 0) {
          newChildNode = {
            ...newChildNode,
            ...getPromptsWithoutSpecificMentionsForNode({
              node: childNode,
              nodesToClearMentions,
            }),
          };
        }

        newChildNode.errors = getNodeErrors({
          node: newChildNode,
          nodes,
          previousNodesPerNode,
        });

        return newChildNode;
      });

      if (property === 'type') {
        if (value === GROUP_LOOP_MERGE) {
          const concepts = getLoopAndMergeConcepts();
          const attributes = getLoopAndMergeAttributes();

          // temporary fix for the loop and merge group
          updatedNode[PROPERTIES.PROPERTY_LOOP_MERGE] = true;
          //
          updatedNode[PROPERTIES.PROPERTY_CONCEPTS] = concepts;
          updatedNode[PROPERTIES.PROPERTY_ATTRIBUTES] = attributes;
          updatedNode[PROPERTIES.PROPERTY_PROPERTIES] =
            getLoopAndMergeProperties({
              repetitions: concepts.length,
              shuffle: SHUFFLE_NONE,
              userDefinedRepetitions: false,
            });
          updatedNode.loopAndMergeFromQuestionConfiguration = {};
          updatedNode.pristineValues = defaultPristineValues(
            true,
            GROUP_LOOP_MERGE,
          );
        } else if (value === GROUP_BASIC) {
          // temporary fix for the loop and merge group
          delete updatedNode[PROPERTIES.PROPERTY_LOOP_MERGE];

          delete updatedNode[PROPERTIES.PROPERTY_CONCEPTS];
          delete updatedNode[PROPERTIES.PROPERTY_ATTRIBUTES];
          delete updatedNode[PROPERTIES.PROPERTY_PROPERTIES];
          delete updatedNode.loopAndMergeFromQuestionConfiguration;

          updatedNode.errors = getNodeErrors({
            node: updatedNode,
            nodes,
            previousNodesPerNode,
          });
        }
      }

      return updatedNode;
    }

    return node;
  });
}

export function cleanLoopAndMergeGroupsFromProfilingQuestions(
  state,
  loopAndMergeQuestionGroups = [],
) {
  if (loopAndMergeQuestionGroups.length === 0) {
    return state;
  }

  const loopAndMergeFromQuestionRefIds = loopAndMergeQuestionGroups.map(
    (group) => group.refId,
  );
  return Object.assign({}, state, {
    surveyDetailsExtended: {
      ...state.surveyDetailsExtended,
      questions: state.surveyDetailsExtended.questions.map((question) => {
        if (loopAndMergeFromQuestionRefIds.includes(question.refId)) {
          return removeLoopAndMergeBasedOnQuestionConfiguration(question);
        }
        return question;
      }),
    },
  });
}
